import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import {
  Phone, PhoneMissed, DollarSign, TrendingUp, CheckCircle2, Briefcase,
  Trophy, ChevronRight, ChevronLeft, Star, ChevronDown, Calculator,
} from "lucide-react";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG   = "#F8F9FA";
const DARK = "#1A1A2E";
const GREEN = "#1A7A4A";
const RED  = "#A32D2D";

const track = (name, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.capture(name, { surface: "why-nearnow", ...props });
    }
  } catch { /* swallow */ }
};

// ---------- Card content ----------
const CARDS = [
  {
    id: "missed-check",
    accent: RED,
    icon: PhoneMissed,
    title: "Every Missed Call Is a Missed Check",
    subtitle: "Most contractors don't lose jobs because they're unqualified.",
    body: [
      "They lose jobs because they never get the chance to quote them.",
      "The homeowner calls. You're on a roof. In a crawl space. Driving between jobs. Working.",
      "You miss the call. They hire someone else.",
      "Not because they're better. Because they answered first.",
    ],
    visual: "missed-call",
    cta: "Next",
  },
  {
    id: "math",
    accent: GOLD,
    icon: DollarSign,
    title: "How Much Is One Missed Call Worth?",
    rows: [
      ["Electrician",        "$5,000+"],
      ["HVAC Contractor",    "$10,000+"],
      ["Roofer",             "$15,000+"],
      ["Kitchen Remodeler",  "$50,000+"],
    ],
    footer: "One missed customer can cost more than years of NearNow.",
    visual: "counter",
    cta: "Show Me The Math",
  },
  {
    id: "compare",
    accent: GOLD,
    icon: Calculator,
    title: "$97 vs. $5,000",
    compare: [
      { label: "NearNow Growth Plan", price: "$97/mo",   tone: "good" },
      { label: "One Electrical Project", price: "$5,000+",  tone: "bad"  },
      { label: "One HVAC Job",          price: "$10,000+", tone: "bad"  },
      { label: "One Roof Replacement",  price: "$15,000+", tone: "bad"  },
      { label: "One Kitchen Remodel",   price: "$50,000+", tone: "bad"  },
    ],
    headline: "Which is riskier?",
    sub: "Paying $97? Or losing thousands because nobody answered?",
    cta: "Why Contractors Join",
  },
  {
    id: "why-join",
    accent: GREEN,
    icon: CheckCircle2,
    title: "Why Contractors Join NearNow",
    checklist: [
      "More visibility",
      "More calls",
      "Faster response times",
      "Better lead capture",
      "More booked estimates",
      "More jobs won",
      "More revenue",
    ],
    headline: "You don't need hundreds of leads.",
    sub: "You need the right homeowner at the right time.",
    cta: "Next",
  },
  {
    id: "work-leads",
    accent: NAVY,
    icon: Briefcase,
    title: "You Work the Job. We Work the Leads.",
    bullets: [
      "Capture leads",
      "Recover missed opportunities",
      "Respond faster",
      "Convert more homeowners",
      "Grow your business",
    ],
    big: ["You Focus On The Work.", "NearNow Focuses On Growth."],
    cta: "Get Started",
  },
  {
    id: "one-customer",
    accent: GOLD,
    icon: Trophy,
    title: "What If NearNow Generates Just One Customer This Year?",
    profitRows: [
      "One electrical project? Profitable.",
      "One HVAC replacement? Profitable.",
      "One roof replacement? Profitable.",
      "One remodeling project? Profitable.",
    ],
    footer: "NearNow doesn't need to generate dozens of customers. It only needs to help you win one.",
    final: "Stop Losing Jobs You Never Knew You Had.",
    primary: "Join NearNow Today",
    secondary: "View Pricing Plans",
  },
];

// ---------- Visual modules ----------
const MissedCallVisual = () => (
  <motion.div
    initial={{ scale: 0.9, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.5 }}
    style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "8px 0 20px" }}
  >
    <motion.div
      animate={{ rotate: [0, -10, 10, -10, 10, 0], scale: [1, 1.06, 1] }}
      transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 1 }}
      style={{
        width: 110, height: 110, borderRadius: "50%", background: "#2a0f12",
        border: `3px solid ${RED}`, display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative",
      }}
    >
      <PhoneMissed size={52} color={RED} />
      <motion.span
        animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        style={{
          position: "absolute", inset: -10, borderRadius: "50%",
          border: `2px solid ${RED}`,
        }}
      />
    </motion.div>
  </motion.div>
);

const RevenueCounter = () => {
  const [val, setVal] = useState(97);
  const targets = useMemo(() => [97, 500, 2000, 5000, 10000, 25000, 50000], []);
  useEffect(() => {
    let idx = 0;
    const t = setInterval(() => {
      idx = (idx + 1) % targets.length;
      setVal(targets[idx]);
    }, 950);
    return () => clearInterval(t);
  }, [targets]);
  return (
    <motion.div
      key={val}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ textAlign: "center", margin: "8px 0 18px" }}
    >
      <div style={{ fontSize: 13, letterSpacing: 2, color: "rgba(255,255,255,0.7)", fontFamily: "Bebas Neue, sans-serif" }}>
        ONE JOB COULD BE WORTH
      </div>
      <div style={{
        fontFamily: "Bebas Neue, sans-serif",
        fontSize: "clamp(48px, 14vw, 96px)",
        color: GOLD, letterSpacing: 2, lineHeight: 1.05,
      }}>
        ${val.toLocaleString()}
      </div>
    </motion.div>
  );
};

const Visual = ({ kind }) => {
  if (kind === "missed-call") return <MissedCallVisual />;
  if (kind === "counter") return <RevenueCounter />;
  return null;
};

// ---------- Single Card ----------
const SwipeCard = ({ card, index, total, onNext, onPrev, onPrimary, onSecondary }) => {
  const Icon = card.icon || ChevronRight;
  return (
    <motion.div
      key={card.id}
      data-testid={`card-${card.id}`}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.3}
      onDragEnd={(_, info) => {
        if (info.offset.x < -80 && index < total - 1) onNext();
        else if (info.offset.x > 80 && index > 0) onPrev();
      }}
      initial={{ opacity: 0, x: 60 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -60 }}
      transition={{ type: "spring", stiffness: 220, damping: 28 }}
      style={{
        background: NAVY,
        color: "#fff",
        borderRadius: 18,
        padding: "26px 22px 32px",
        boxShadow: "0 20px 60px rgba(10,22,40,0.35)",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${card.accent}40`,
        minHeight: 560,
        cursor: "grab",
      }}
    >
      {/* corner glow */}
      <div style={{
        position: "absolute", top: -80, right: -80, width: 220, height: 220,
        background: `${card.accent}33`, filter: "blur(60px)", borderRadius: "50%",
      }} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, position: "relative" }}>
        <div style={{
          background: `${card.accent}22`, border: `1px solid ${card.accent}`,
          borderRadius: 10, padding: 8, display: "inline-flex",
        }}>
          <Icon size={20} color={card.accent} />
        </div>
        <span style={{
          color: card.accent, fontFamily: "Bebas Neue, sans-serif",
          letterSpacing: 3, fontSize: 13,
        }}>
          {index + 1} / {total}
        </span>
      </div>

      <h2 style={{
        fontFamily: "Bebas Neue, sans-serif",
        fontSize: "clamp(28px, 7.5vw, 44px)",
        margin: 0, lineHeight: 1.05, letterSpacing: 1,
        position: "relative",
      }}>
        {card.title}
      </h2>

      {card.subtitle && (
        <p style={{ color: "#cbd0db", fontSize: 16, marginTop: 10, lineHeight: 1.45 }}>
          {card.subtitle}
        </p>
      )}

      {card.visual && <Visual kind={card.visual} />}

      {card.body && (
        <div style={{ marginTop: 12 }}>
          {card.body.map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i, duration: 0.35 }}
              style={{ color: "#fff", fontSize: 17, margin: "0 0 10px", lineHeight: 1.5 }}
            >
              {line}
            </motion.p>
          ))}
        </div>
      )}

      {card.rows && (
        <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
          {card.rows.map(([trade, val], i) => (
            <motion.div
              key={trade}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 * i }}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${card.accent}55`,
                borderRadius: 10,
                padding: "14px 16px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <span style={{ fontSize: 17, fontWeight: 500 }}>{trade}</span>
              <span style={{ fontFamily: "Bebas Neue, sans-serif", color: card.accent, fontSize: 24, letterSpacing: 1 }}>
                {val}
              </span>
            </motion.div>
          ))}
        </div>
      )}

      {card.compare && (
        <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
          {card.compare.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.07 * i }}
              style={{
                background: c.tone === "good" ? "rgba(26,122,74,0.16)" : "rgba(163,45,45,0.14)",
                border: `1px solid ${c.tone === "good" ? GREEN : RED}66`,
                borderRadius: 10, padding: "14px 16px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <span style={{ fontSize: 16 }}>{c.label}</span>
              <span style={{
                fontFamily: "Bebas Neue, sans-serif",
                color: c.tone === "good" ? GREEN : RED,
                fontSize: 22, letterSpacing: 1,
              }}>
                {c.price}
              </span>
            </motion.div>
          ))}
        </div>
      )}

      {card.checklist && (
        <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
          {card.checklist.map((line, i) => (
            <motion.li
              key={line}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 * i }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
            >
              <CheckCircle2 size={20} color={GREEN} />
              <span style={{ fontSize: 17 }}>{line}</span>
            </motion.li>
          ))}
        </ul>
      )}

      {card.bullets && (
        <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 18px" }}>
          {card.bullets.map((line, i) => (
            <motion.li
              key={line}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.08 * i }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0" }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD }} />
              <span style={{ fontSize: 17 }}>{line}</span>
            </motion.li>
          ))}
        </ul>
      )}

      {card.big && (
        <div style={{ marginTop: 16 }}>
          {card.big.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 * i, duration: 0.5 }}
              style={{
                fontFamily: "Bebas Neue, sans-serif",
                fontSize: "clamp(26px, 7vw, 38px)",
                letterSpacing: 1.2, color: i === 0 ? "#fff" : GOLD,
                lineHeight: 1.1, marginBottom: 6,
              }}
            >
              {line}
            </motion.div>
          ))}
        </div>
      )}

      {card.profitRows && (
        <div style={{ marginTop: 12 }}>
          {card.profitRows.map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 * i }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px", borderRadius: 8,
                background: "rgba(212,175,55,0.08)",
                marginBottom: 8, fontSize: 16,
              }}
            >
              <CheckCircle2 size={18} color={GOLD} /> {line}
            </motion.div>
          ))}
        </div>
      )}

      {card.headline && (
        <p style={{ fontSize: 22, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1, color: GOLD, marginTop: 18, marginBottom: 4 }}>
          {card.headline}
        </p>
      )}
      {card.sub && (
        <p style={{ color: "#cbd0db", fontSize: 16, lineHeight: 1.5 }}>
          {card.sub}
        </p>
      )}
      {card.footer && (
        <p style={{ color: "#cbd0db", fontSize: 16, marginTop: 14, lineHeight: 1.5 }}>
          {card.footer}
        </p>
      )}
      {card.final && (
        <p style={{
          fontFamily: "Bebas Neue, sans-serif",
          fontSize: "clamp(28px, 7.5vw, 42px)",
          color: GOLD, letterSpacing: 1, lineHeight: 1.1, marginTop: 18,
        }}>
          {card.final}
        </p>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
        {card.primary && (
          <button
            data-testid="card-primary-cta"
            onClick={onPrimary}
            className="btn-gold tap"
            style={{ flex: 1, minHeight: 56, fontSize: 16 }}
          >
            {card.primary}
          </button>
        )}
        {card.secondary && (
          <button
            data-testid="card-secondary-cta"
            onClick={onSecondary}
            className="tap"
            style={{
              flex: 1, minHeight: 56, fontSize: 14, background: "transparent",
              color: "#fff", border: `1px solid ${GOLD}`, borderRadius: 8, cursor: "pointer",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
            }}
          >
            {card.secondary}
          </button>
        )}
        {!card.primary && card.cta && index < total - 1 && (
          <button
            data-testid="card-next-cta"
            onClick={onNext}
            className="btn-gold tap"
            style={{ flex: 1, minHeight: 56, fontSize: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {card.cta} <ChevronRight size={20} />
          </button>
        )}
      </div>
    </motion.div>
  );
};

// ---------- Testimonials ----------
const TESTIMONIALS = [
  {
    name: "Mike R.",
    trade: "Electrician",
    avatar: "M",
    stars: 5,
    quote: "Two missed calls last month would have cost me $8,000. NearNow caught them both before they hired someone else.",
  },
  {
    name: "Dave T.",
    trade: "HVAC Contractor",
    avatar: "D",
    stars: 5,
    quote: "I was skeptical about $97/mo. First weekend, NearNow recovered a $4,200 furnace install I would have lost. Done.",
  },
  {
    name: "Tony S.",
    trade: "Roofer",
    avatar: "T",
    stars: 5,
    quote: "Storm season I miss calls all day. NearNow texts them back in 60 seconds. Booked 3 roof replacements in one week.",
  },
];

// ---------- ROI Calculator ----------
const ROICalc = () => {
  const [jobValue, setJobValue] = useState(5000);
  const [monthlyLeads, setMonthlyLeads] = useState(40);
  const [missedPct, setMissedPct] = useState(30); // % of calls missed
  const lost = useMemo(() => {
    const missed = (monthlyLeads * missedPct) / 100;
    return Math.round(missed * jobValue * 0.25); // 25% close rate assumption
  }, [jobValue, monthlyLeads, missedPct]);
  const recovered = Math.round(lost * 0.6); // NearNow recovers ~60% of missed
  const yearly = recovered * 12;
  const cost = 97 * 12;
  const roi = cost > 0 ? Math.round(((yearly - cost) / cost) * 100) : 0;

  useEffect(() => { track("why_roi_input", { jobValue, monthlyLeads, missedPct }); }, [jobValue, monthlyLeads, missedPct]);

  return (
    <div data-testid="roi-calculator" style={{
      background: "#fff", borderRadius: 14, padding: 22, border: `1px solid ${GOLD}33`,
      boxShadow: "0 10px 30px rgba(10,22,40,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Calculator color={GOLD} size={22} />
        <h3 style={{ margin: 0, fontFamily: "Bebas Neue, sans-serif", fontSize: 26, color: NAVY, letterSpacing: 1 }}>
          ROI Calculator
        </h3>
      </div>

      <Field label="Average Job Value ($)">
        <input data-testid="roi-job-value" className="nn-input" type="number" min="0"
          value={jobValue} onChange={(e) => setJobValue(Number(e.target.value) || 0)} />
      </Field>
      <Field label="Average Monthly Leads">
        <input data-testid="roi-monthly-leads" className="nn-input" type="number" min="0"
          value={monthlyLeads} onChange={(e) => setMonthlyLeads(Number(e.target.value) || 0)} />
      </Field>
      <Field label={`Estimated Missed Calls (${missedPct}%)`}>
        <input data-testid="roi-missed-pct" type="range" min="0" max="80" step="5"
          value={missedPct} onChange={(e) => setMissedPct(Number(e.target.value))}
          style={{ width: "100%", accentColor: GOLD, minHeight: 44 }} />
      </Field>

      <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
        <ROIRow label="Potential Revenue Lost (yearly)" value={`$${(lost * 12).toLocaleString()}`} tone="bad" testid="roi-lost" />
        <ROIRow label="Recovered with NearNow (yearly)" value={`$${yearly.toLocaleString()}`} tone="good" testid="roi-recovered" />
        <ROIRow label="Estimated NearNow ROI" value={`${roi.toLocaleString()}%`} tone="good" testid="roi-pct" big />
      </div>

      <p style={{ marginTop: 14, color: "#5a5d96", fontSize: 13 }}>
        Assumes 25% close rate · NearNow recovers ~60% of missed opportunities with 60-second SMS callback.
      </p>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="nn-label" style={{ display: "block", marginBottom: 6, color: NAVY }}>
      {label}
    </label>
    {children}
  </div>
);

const ROIRow = ({ label, value, tone, big, testid }) => (
  <div data-testid={testid} style={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: tone === "good" ? "rgba(26,122,74,0.08)" : "rgba(163,45,45,0.06)",
    borderLeft: `4px solid ${tone === "good" ? GREEN : RED}`,
    padding: "12px 14px", borderRadius: 8,
  }}>
    <span style={{ color: NAVY, fontSize: 14 }}>{label}</span>
    <span style={{
      color: tone === "good" ? GREEN : RED,
      fontFamily: "Bebas Neue, sans-serif",
      fontSize: big ? 30 : 22, letterSpacing: 1,
    }}>{value}</span>
  </div>
);

// ---------- FAQ ----------
const FAQ_ITEMS = [
  {
    q: "How does NearNow help me get more leads?",
    a: "We capture every call you miss within 60 seconds via SMS, follow up automatically, push your business to homeowners searching nearby, and book estimates straight to your calendar.",
  },
  {
    q: "How quickly can I get started?",
    a: "Five minutes. Add your business, pick a plan, enter your card. The first month is free — your number gets active SMS capture the same day.",
  },
  {
    q: "Do I need a website?",
    a: "No. Most NearNow contractors don't have one. We give you a verified profile, a booking link, and a phone-first dashboard. That's it.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. One tap in your dashboard. No phone calls, no retention scripts, no fees.",
  },
  {
    q: "How much revenue can NearNow help recover?",
    a: "Most contractors recover one job in the first 30 days. For an electrician, that's $5,000+ on a $97 plan — payback in week one. Use the calculator above to estimate for your trade.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="why-faq" style={{ display: "grid", gap: 10 }}>
      {FAQ_ITEMS.map((item, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            data-testid={`faq-item-${i}`}
            style={{
              background: "#fff", borderRadius: 12, overflow: "hidden",
              border: `1px solid ${isOpen ? GOLD : "#e5e7ee"}`,
              transition: "border-color 0.2s",
            }}
          >
            <button
              data-testid={`faq-toggle-${i}`}
              onClick={() => { setOpen(isOpen ? -1 : i); track("why_faq_open", { question: item.q }); }}
              className="tap"
              style={{
                width: "100%", textAlign: "left", padding: "16px 18px",
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                minHeight: 56, gap: 10,
              }}
            >
              <span style={{ color: NAVY, fontWeight: 600, fontSize: 16 }}>{item.q}</span>
              <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={20} color={GOLD} />
              </motion.div>
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ padding: "0 18px 18px", color: DARK, fontSize: 15, lineHeight: 1.55 }}>
                    {item.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Main Page ----------
const WhyNearNow = () => {
  const [idx, setIdx] = useState(0);
  const [scrollDepth, setScrollDepth] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    track("why_page_view");
  }, []);

  useEffect(() => {
    track("why_card_view", { index: idx, card_id: CARDS[idx]?.id });
  }, [idx]);

  // scroll depth tracking
  useEffect(() => {
    const onScroll = () => {
      const sd = Math.round((window.scrollY / Math.max(document.body.scrollHeight - window.innerHeight, 1)) * 100);
      if (sd > scrollDepth && [25, 50, 75, 90].includes(sd)) {
        track("why_scroll_depth", { depth: sd });
        setScrollDepth(sd);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollDepth]);

  const goJoin = () => { track("why_cta_join"); nav("/onboard-simple"); };
  const goPricing = () => { track("why_cta_pricing"); nav("/pricing"); };

  const next = () => setIdx((i) => Math.min(i + 1, CARDS.length - 1));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  const card = CARDS[idx];

  return (
    <div data-testid="why-nearnow-page" style={{ background: BG, minHeight: "100vh" }}>
      {/* HERO band */}
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #060d1a 100%)`,
        color: "#fff", padding: "32px 16px 24px",
        borderBottom: `3px solid ${GOLD}`, textAlign: "center",
      }}>
        <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 4, margin: 0, fontSize: 14 }}>
          MORE SALES WITH NEARNOW
        </p>
        <h1 style={{
          fontFamily: "Bebas Neue, sans-serif",
          fontSize: "clamp(34px, 9vw, 64px)",
          letterSpacing: 1, margin: "6px 0 4px", lineHeight: 1.05,
        }}>
          Why Get NearNow Today
        </h1>
        <p style={{ color: "#cbd0db", fontSize: 15, marginTop: 6 }}>
          Every Missed Call Is a Missed Check.
        </p>
      </section>

      {/* SWIPER */}
      <section style={{ padding: "22px 16px 24px", maxWidth: 560, margin: "0 auto" }}>
        <AnimatePresence mode="wait">
          <SwipeCard
            card={card}
            index={idx}
            total={CARDS.length}
            onNext={next}
            onPrev={prev}
            onPrimary={goJoin}
            onSecondary={goPricing}
          />
        </AnimatePresence>

        {/* Progress dots */}
        <div data-testid="card-progress" style={{
          display: "flex", justifyContent: "center", gap: 8, marginTop: 20,
        }}>
          {CARDS.map((c, i) => (
            <button
              key={c.id}
              data-testid={`progress-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Go to card ${i + 1}`}
              className="tap"
              style={{
                width: i === idx ? 28 : 10, height: 10, borderRadius: 5,
                background: i === idx ? GOLD : "#cbd0db",
                border: "none", cursor: "pointer",
                transition: "all 0.25s ease",
                minWidth: 10, minHeight: 24, padding: 0,
              }}
            />
          ))}
        </div>

        {/* Prev/Next controls */}
        <div style={{ display: "flex", gap: 10, marginTop: 14, justifyContent: "space-between" }}>
          <button
            data-testid="card-prev"
            onClick={prev}
            disabled={idx === 0}
            className="tap"
            style={{
              minHeight: 48, padding: "0 16px",
              background: "transparent", color: idx === 0 ? "#cbd0db" : NAVY,
              border: `1px solid ${idx === 0 ? "#cbd0db" : NAVY}`,
              borderRadius: 8, cursor: idx === 0 ? "not-allowed" : "pointer",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <ChevronLeft size={18} /> PREV
          </button>
          <button
            data-testid="card-next"
            onClick={next}
            disabled={idx === CARDS.length - 1}
            className="tap"
            style={{
              minHeight: 48, padding: "0 16px",
              background: idx === CARDS.length - 1 ? "#cbd0db" : NAVY,
              color: "#fff", border: "none", borderRadius: 8,
              cursor: idx === CARDS.length - 1 ? "not-allowed" : "pointer",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            NEXT <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: "32px 16px 8px", maxWidth: 980, margin: "0 auto" }}>
        <SectionHeader eyebrow="REAL CONTRACTORS" title="What They Say" />
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14, marginTop: 18,
        }}>
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              style={{
                background: "#fff", borderRadius: 14, padding: 20,
                border: `1px solid ${GOLD}33`,
                boxShadow: "0 10px 24px rgba(10,22,40,0.06)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: NAVY, color: GOLD, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontFamily: "Bebas Neue, sans-serif", fontSize: 22, letterSpacing: 1,
                }}>
                  {t.avatar}
                </div>
                <div>
                  <div style={{ color: NAVY, fontWeight: 700, fontSize: 16 }}>{t.name}</div>
                  <div style={{ color: "#5a5d96", fontSize: 13 }}>{t.trade}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 2, marginBottom: 8 }}>
                {Array.from({ length: t.stars }).map((_, k) => (
                  <Star key={k} size={16} fill={GOLD} color={GOLD} />
                ))}
              </div>
              <p style={{ color: DARK, fontSize: 15, lineHeight: 1.55, margin: 0 }}>
                "{t.quote}"
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section style={{ padding: "28px 16px 8px", maxWidth: 720, margin: "0 auto" }}>
        <SectionHeader eyebrow="DO THE MATH" title="What Are Missed Calls Costing You?" />
        <div style={{ marginTop: 18 }}>
          <ROICalc />
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "28px 16px 120px", maxWidth: 720, margin: "0 auto" }}>
        <SectionHeader eyebrow="QUESTIONS" title="Frequently Asked" />
        <div style={{ marginTop: 18 }}>
          <FAQ />
        </div>
      </section>

      {/* Sticky CTA Footer */}
      <div
        data-testid="why-sticky-cta"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "rgba(10,22,40,0.96)",
          backdropFilter: "blur(8px)",
          borderTop: `2px solid ${GOLD}`,
          padding: "12px 14px",
          display: "flex", gap: 10, zIndex: 40,
        }}
      >
        <button
          data-testid="sticky-join"
          onClick={goJoin}
          className="btn-gold tap"
          style={{ flex: 2, minHeight: 52, fontSize: 16 }}
        >
          JOIN NEARNOW TODAY
        </button>
        <button
          data-testid="sticky-pricing"
          onClick={goPricing}
          className="tap"
          style={{
            flex: 1, minHeight: 52, fontSize: 14, background: "transparent",
            color: "#fff", border: `1px solid ${GOLD}`, borderRadius: 8, cursor: "pointer",
            fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
          }}
        >
          VIEW PLANS
        </button>
      </div>
    </div>
  );
};

const SectionHeader = ({ eyebrow, title }) => (
  <div style={{ textAlign: "center" }}>
    <p style={{ color: GOLD, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, margin: 0, fontSize: 12 }}>
      {eyebrow}
    </p>
    <h2 style={{
      fontFamily: "Bebas Neue, sans-serif",
      fontSize: "clamp(26px, 6vw, 38px)",
      color: NAVY, letterSpacing: 1, margin: "4px 0 0", lineHeight: 1.05,
    }}>
      {title}
    </h2>
  </div>
);

export default WhyNearNow;
